import type {
  FieldConfig,
  FieldHelperProps,
  FieldInputProps,
  FieldMetaProps,
  FormikErrors,
  FormikState,
  FormikTouched,
} from 'formik'
import type * as v from 'yup-intl-validator'
import type { IIntlShapeRich } from '@/hooks/useIntlRich'

export type TValidatorObject<T extends v.ObjectShape = {}> = (
  intl: IIntlShapeRich,
  required?: boolean,
) => v.yup.ObjectSchema<T>

export type TValidator = (intl: IIntlShapeRich, required?: boolean) => v.yup.AnySchema

export type TValidatorSchema = Record<string, TValidator>

export interface TGrailsValidationError {
  total: number
  _embedded: {
    errors: {
      message: string
      path: string
      _links: {
        self: {
          href: string
        }
      }
    }[]
  }
}

export enum EClass {
  Aircraft = 'Aircraft',
  AircraftConfiguration = 'AircraftConfiguration',
  AircraftConfigurationAvailability = 'AircraftConfigurationAvailability',
  AircraftConfigurationInstallation = 'AircraftConfigurationInstallation',
  AircraftMonthlyFee = 'AircraftMonthlyFee',
  AircraftRate = 'AircraftRate',
  AircraftType = 'AircraftType',
  Airport = 'Airport',
  AirportCostEstimate = 'AirportCostEstimate',
  BaseLocation = 'BaseLocation',
  CarRefueling = 'CarRefueling',
  Country = 'Country',
  CrewCompositionRule = 'CrewCompositionRule',
  CrewDailyAllowance = 'CrewDailyAllowance',
  CrewDailyAllowanceTable = 'CrewDailyAllowanceTable',
  CrewSeating = 'CrewSeating',
  CrewWage = 'CrewWage',
  DigitalFile = 'DigitalFile',
  Entity = 'Entity',
  Expense = 'Expense',
  ExpenseCategory = 'ExpenseCategory',
  ExpenseCategoryConfiguration = 'ExpenseCategoryConfiguration',
  ExpenseDraft = 'ExpenseDraft',
  ExpenseDraftWarningMealOverlapping = 'ExpenseDraftWarningMealOverlapping',
  ExpenseDraftWarningMealSimilar = 'ExpenseDraftWarningMealSimilar',
  CrewMealExpenseDraft = 'CrewMealExpenseDraft',
  ExpenseItem = 'ExpenseItem',
  Flight = 'Flight',
  FlightShare = 'FlightShare',
  GroundEvent = 'GroundEvent',
  HotelStay = 'HotelStay',
  MemberRate = 'MemberRate',
  Membership = 'Membership',
  MembershipSubscription = 'MembershipSubscription',
  MembershipSubscriptionAssociatedMember = 'MembershipSubscriptionAssociatedMember',
  MenuItem = 'MenuItem',
  Mission = 'Mission',
  MissionForfait = 'MissionForfait',
  MonthlyExperience = 'MonthlyExperience',
  MonthlyExperienceByAircraftType = 'MonthlyExperienceByAircraftType',
  Organisation = 'Organisation',
  Person = 'Person',
  PilotQualification = 'PilotQualification',
  Refueling = 'Refueling',
  Role = 'Role',
  Timer = 'Timer',
  TimerIndex = 'TimerIndex',
  User = 'User',
  UserExactToken = 'UserExactToken',
  UserRole = 'UserRole',
  AircraftMonthlyStatement = 'AircraftMonthlyStatement',

  AccountMouvement = 'AccountMouvement',
  AccountMouvementDocument = 'AccountMouvementDocument',
  AccountMouvementFiscalDocument = 'AccountMouvementFiscalDocument',
  AccountMouvementPayment = 'AccountMouvementPayment',
  AircraftOwnerInvoice = 'AircraftOwnerInvoice',
  AircraftOwnerInvoiceItem = 'AircraftOwnerInvoiceItem',
  DayMissionInvoiceItem = 'DayMissionInvoiceItem',
  FinancialYear = 'FinancialYear',
  FiscalPeriod = 'FiscalPeriod',
  FlightMissionInvoiceItem = 'FlightMissionInvoiceItem',
  FuelConsumptionDistribution = 'FuelConsumptionDistribution',
  FuelCostReference = 'FuelCostReference',
  FuelDifferenceComputationDetail = 'FuelDifferenceComputationDetail',
  Instalment = 'Instalment',
  IntermediaryAgencyPaymentCollection = 'IntermediaryAgencyPaymentCollection',
  Invoice = 'Invoice',
  InvoiceItem = 'InvoiceItem',
  Loan = 'Loan',
  LoanMovement = 'LoanMovement',
  MembershipSubscriptionAircraftMonthlyFee = 'MembershipSubscriptionAircraftMonthlyFee',
  MissionInvoice = 'MissionInvoice',
  MissionInvoiceItem = 'MissionInvoiceItem',
  PaymentCondition = 'PaymentCondition',
  PersonalExpenseAccountMouvementDocument = 'PersonalExpenseAccountMouvementDocument',

  AccountingChartsAccount = 'AccountingChartsAccount',

  AssetAcquisitionAgreement = 'AssetAcquisitionAgreement',
  AssetResource = 'AssetResource',
  CarResource = 'CarResource',
  FuelCardResource = 'FuelCardResource',
  ITResource = 'ITResource',
  SIMCardResource = 'SIMCardResource',
  AssetResourceAssignment = 'AssetResourceAssignment',
  CarResourceAssignment = 'CarResourceAssignment',
  AssetResourceRemark = 'AssetResourceRemark',
  CarOdometerReport = 'CarOdometerReport',

  CrewAnalyticsAverage = 'CrewAnalyticsAverage',
  CrewAnalyticsCategory = 'CrewAnalyticsCategory',

  CrewDailyRevenue = 'CrewDailyRevenue',
  CrewMonthlyStatement = 'CrewMonthlyStatement',

  AircraftTypeCrewRate = 'AircraftTypeCrewRate',
  CrewAssignment = 'CrewAssignment',
  CrewCategory = 'CrewCategory',
  CrewCategoryRate = 'CrewCategoryRate',
  CrewRate = 'CrewRate',
  CrewType = 'CrewType',
  Indexation = 'Indexation',

  EmailRecipient = 'EmailRecipient',

  BankAccountStatement = 'BankAccountStatement',
  CreditCardMonthlyStatement = 'CreditCardMonthlyStatement',
  ExternalMonthlyStatement = 'ExternalMonthlyStatement',
  MonthlyStatement = 'MonthlyStatement',
  PersonalExpenseMonthlyStatement = 'PersonalExpenseMonthlyStatement',

  BankAccountPayment = 'BankAccountPayment',
  BankAccountTransferFileRequest = 'BankAccountTransferFileRequest',
  CashPayment = 'CashPayment',
  CreditCardPayment = 'CreditCardPayment',
  IntermediaryAgencyPayment = 'IntermediaryAgencyPayment',
  InternalPayment = 'InternalPayment',
  Payment = 'Payment',
  PersonalExpenseMonthlyStatementPayment = 'PersonalExpenseMonthlyStatementPayment',
  PersonalExpensePayment = 'PersonalExpensePayment',

  BankAccount = 'BankAccount',
  CreditCard = 'CreditCard',
  Domiciliation = 'Domiciliation',
  PaymentMean = 'PaymentMean',

  LabelSetting = 'LabelSetting',

  QuoteDay = 'QuoteDay',
  QuoteFlight = 'QuoteFlight',
  QuoteMission = 'QuoteMission',

  UserAgenda = 'UserAgenda',
  UserAgendaEvent = 'UserAgendaEvent',
  UserAgendaGroundCourseEvent = 'UserAgendaGroundCourseEvent',
  UserAgendaFlightEvent = 'UserAgendaFlightEvent',
  UserAgendaOfficeDutyEvent = 'UserAgendaOfficeDutyEvent',
  UserAgendaPositioningEvent = 'UserAgendaPositioningEvent',
  UserAgendaTrainingEvent = 'UserAgendaTrainingEvent',
  UserAgendaWarning = 'UserAgendaWarning',

  CrewSummary = 'CrewSummary',

  BelgianDepartingFlightTaxesExpense = 'BelgianDepartingFlightTaxesExpense',
  BelgianDepartingFlightTaxesExpenseItem = 'BelgianDepartingFlightTaxesExpenseItem',
  ItalianFlightTaxesExpense = 'ItalianFlightTaxesExpense',
  ItalianFlightTaxesExpenseItem = 'ItalianFlightTaxesExpenseItem',

  ExactPayment = 'ExactPayment',
  ExactSync = 'ExactSync',
  ExactSyncQueue = 'ExactSyncQueue',
  ExactVATCode = 'ExactVATCode',

  SkylegsSync = 'SkylegsSync',
  SkylegsSyncQueue = 'SkylegsSyncQueue',

  Passenger = 'Passenger',
  PassengerAssignment = 'PassengerAssignment',

  HotelBooking = 'HotelBooking',
  HotelBookingAssignment = 'HotelBookingAssignment',
  HotelRating = 'HotelRating',

  AirportProvider = 'AirportProvider',
  Hotel = 'Hotel',

  CrewMealShare = 'CrewMealShare',

  RosterTemplate = 'RosterTemplate',
  RosterTemplateDayPosition = 'RosterTemplateDayPosition',
  RosterTemplateAssignment = 'RosterTemplateAssignment',
  RosterCategory = 'RosterCategory',
  RosterDay = 'RosterDay',
  RosterCategoryWarning = 'RosterCategoryWarning',
  RosterUserAgenda = 'RosterUserAgenda',

  LeaveRequest = 'LeaveRequest',
  PublicHolidayLeave = 'PublicHolidayLeave',
  LeaveRequestDay = 'LeaveRequestDay',
  LeaveType = 'LeaveType',
  SKLCategory = 'SKLCategory',
  LeaveAssignment = 'LeaveAssignment',
  LeaveAssignmentTemplate = 'LeaveAssignmentTemplate',
  LeaveConsumption = 'LeaveConsumption',

  Runway = 'Runway',
  PerformanceStep = 'PerformanceStep',

  QuotePassenger = 'QuotePassenger',

  CrewDutyAnalytics = 'CrewDutyAnalytics',
  CategoryDutyAnalytics = 'CategoryDutyAnalytics',
  CrewDutyBalance = 'CrewDutyBalance',
  CrewTypeDutyBalance = 'CrewTypeDutyBalance',
}

export const ClassValues = Object.values(EClass)

export interface IFormik<Data> {
  initialValues: Data
  initialErrors: FormikErrors<unknown>
  initialTouched: FormikTouched<unknown>
  initialStatus: unknown
  handleBlur: {
    (e: React.FocusEvent<unknown>): void
    <T = unknown>(fieldOrEvent: T): T extends string ? (e: unknown) => void : void
  }
  handleChange: {
    (e: React.ChangeEvent<unknown>): void
    <T = string | React.ChangeEvent<unknown>>(
      field: T,
    ): T extends React.ChangeEvent<unknown>
      ? void
      : (e: string | React.ChangeEvent<unknown>) => void
  }
  handleReset: (e: unknown) => void
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void
  resetForm: (nextState?: Partial<FormikState<Data>> | undefined) => void
  setErrors: (errors: FormikErrors<Data>) => void
  setFormikState: (
    stateOrCb: FormikState<Data> | ((state: FormikState<Data>) => FormikState<Data>),
  ) => void
  setFieldTouched: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean | undefined,
  ) => Promise<FormikErrors<Data>> | Promise<void>
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean | undefined,
  ) => Promise<FormikErrors<Data>> | Promise<void>
  setFieldError: (field: string, value: string | undefined) => void
  setStatus: (status: unknown) => void
  setSubmitting: (isSubmitting: boolean) => void
  setTouched: (
    touched: FormikTouched<Data>,
    shouldValidate?: boolean | undefined,
  ) => Promise<FormikErrors<Data>> | Promise<void>
  setValues: (
    values: React.SetStateAction<Data>,
    shouldValidate?: boolean | undefined,
  ) => Promise<FormikErrors<Data>> | Promise<void>
  submitForm: () => Promise<Data | undefined>
  validateForm: (values?: Data) => Promise<FormikErrors<Data>>
  validateField: (name: string) => Promise<void> | Promise<string | undefined>
  isValid: boolean
  dirty: boolean
  unregisterField: (name: string) => void
  registerField: (name: string, { validate }: { validate: unknown }) => void
  getFieldProps: (nameOrOptions: string | FieldConfig<unknown>) => FieldInputProps<unknown>
  getFieldMeta: (name: string) => FieldMetaProps<unknown>
  getFieldHelpers: (name: string) => FieldHelperProps<unknown>
  validateOnBlur: boolean
  validateOnChange: boolean
  validateOnMount: boolean
  values: Data
  errors: FormikErrors<Data>
  touched: FormikTouched<Data>
  isSubmitting: boolean
  isValidating: boolean
  status?: unknown
  submitCount: number
}
